// 노트 관리 관련 함수들
import {
    getNoteTimingParams,
    convertNoteTypeToExternal,
    convertExternalToNoteType,
    calculateLongNoteTime,
    beatToTime,
    timeToBeat,
    calculateSectionOffsets,
    recomputeSectionIndices,
    clampAngleOffset,
    MAX_ANGLE_OFFSET,
    normalizeBezier
} from './utils.js';

const MUSIC_START_TIME = 3.0;

// 노트 검증
export function validateNote(note, globalBpm, globalSubdivisions) {
    const errors = [];
    
    if (typeof note.beat !== 'number' || isNaN(note.beat)) {
        errors.push('Invalid beat value');
    }
    
    if (!note.type || typeof note.type !== 'string') {
        errors.push('Invalid note type');
    }
    
    if (note.type.includes('direction') || note.type.includes('both')) {
        if (!note.direction || typeof note.direction !== 'string') {
            errors.push('Direction notes must have a valid direction');
        }
    }

    // Node 노트는 direction이 옵션 (기본 'none'). 값이 있다면 문자열이어야 함
    if (note.type === 'node' && note.direction !== undefined && typeof note.direction !== 'string') {
        errors.push('Node direction must be a string when provided');
    }
    
    if (note.isLong && (!note.longTime || note.longTime <= 0)) {
        errors.push('Long notes must have positive longTime');
    }

    if (note.angleOffset !== undefined && note.angleOffset !== null) {
        const n = Number(note.angleOffset);
        if (!Number.isFinite(n)) {
            errors.push('angleOffset must be a number');
        } else if (Math.abs(n) > MAX_ANGLE_OFFSET) {
            errors.push(`angleOffset must be within ±${MAX_ANGLE_OFFSET} degrees`);
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
}

// direction이 미지정이면 그 노트의 angleOffset은 상속값에 덮여 쓰이지 않는다.
// 값이 조용히 무시되는 상태라 경고로 드러낸다 — isWait 노트의 bezier를 무시할 때와 같은 처우.
export function findIgnoredAngleOffsets(notes) {
    const PATH_TYPES = ["direction", "both", "longdirection", "longboth", "node"];
    return notes.reduce((hits, note, index) => {
        const hasOwnAngle = Boolean(note.angleOffset);
        const noDirection = !note.direction || note.direction === "none";
        if (hasOwnAngle && noDirection && PATH_TYPES.includes(note.type)) {
            hits.push({ index, angleOffset: note.angleOffset });
        }
        return hits;
    }, []);
}

// 차트 전체 검증
export function validateChart(notes, globalBpm, globalSubdivisions, preDelaySeconds) {
    const errors = [];
    const warnings = [];
    const validatedNotes = [];

    // 시작점 확인 (구간 0의 beat 0 direction 노트)
    const startNote = notes.find(n => n.beat === 0 && n.type === "direction");
    if (!startNote) {
        errors.push('Chart must have a starting direction note at beat 0');
    }

    // 각 노트 검증
    notes.forEach((note, index) => {
        const validation = validateNote(note, globalBpm, globalSubdivisions);
        if (!validation.isValid) {
            errors.push(`Note ${index}: ${validation.errors.join(', ')}`);
        } else {
            // 원본 인덱스를 _origIndex로 보존 (section offset 매핑용)
            validatedNotes.push({ ...note, _origIndex: index });
        }
    });

    // 구간 오프셋 계산 (원본 notes 배열 기준)
    const sectionOffsets = calculateSectionOffsets(notes, globalBpm, globalSubdivisions);

    // 시간 순서 검증 및 Long Note 겹침 검사 (절대시간 기준)
    const sortedNotes = [...validatedNotes].sort((a, b) => {
        const aOff = sectionOffsets[a._origIndex] || 0;
        const bOff = sectionOffsets[b._origIndex] || 0;
        const aTime = aOff + beatToTime(a.beat, a.bpm || globalBpm, a.subdivisions || globalSubdivisions);
        const bTime = bOff + beatToTime(b.beat, b.bpm || globalBpm, b.subdivisions || globalSubdivisions);
        return aTime - bTime;
    });

    // Long Note 겹침 및 범위 내 노트 검증
    for (let i = 0; i < sortedNotes.length - 1; i++) {
        const currentNote = sortedNotes[i];
        if (currentNote.isLong && currentNote.longTime > 0) {
            const currentTiming = getNoteTimingParams(currentNote, globalBpm, globalSubdivisions);
            const currentOff = sectionOffsets[currentNote._origIndex] || 0;
            const currentStartTime = currentOff + beatToTime(currentNote.beat, currentTiming.bpm, currentTiming.subdivisions);
            const currentEndTime = currentStartTime + calculateLongNoteTime(currentNote, globalBpm, globalSubdivisions);

            // 현재 Long Note의 타입 확인
            const currentLongType = currentNote.type;

            // 다음 노트들이 현재 Long Note 범위 안에 있는지 검사
            for (let j = i + 1; j < sortedNotes.length; j++) {
                const nextNote = sortedNotes[j];
                const nextTiming = getNoteTimingParams(nextNote, globalBpm, globalSubdivisions);
                const nextOff = sectionOffsets[nextNote._origIndex] || 0;
                const nextTime = nextOff + beatToTime(nextNote.beat, nextTiming.bpm, nextTiming.subdivisions);

                // 부동소수점 정밀도 문제를 해결하기 위해 작은 오차 허용
                const EPSILON = 1e-10;

                if (nextTime < currentEndTime - EPSILON) {
                    // 다른 Long Note와의 겹침 검사 (에러)
                    if (nextNote.isLong && nextNote.longTime > 0) {
                        errors.push(`Error: Long Note ${nextNote._origIndex} (beat ${nextNote.beat}) overlaps with Long Note ${currentNote._origIndex} (beat ${currentNote.beat})`);
                    }
                    // 롱노트 범위 내 허용 노트 검증
                    else {
                        const nextNoteType = nextNote.type;
                        let isValidInRange = false;

                        if (currentLongType === 'longtab') {
                            isValidInRange = (nextNoteType === 'tab' || nextNoteType === 'direction' || nextNoteType === 'both' || nextNoteType === 'node');
                        } else if (currentLongType === 'longdirection') {
                            isValidInRange = (nextNoteType === 'tab' || nextNoteType === 'node');
                        } else if (currentLongType === 'longboth') {
                            isValidInRange = (nextNoteType === 'tab' || nextNoteType === 'node');
                        }

                        if (!isValidInRange) {
                            errors.push(`Error: Note ${nextNote._origIndex} (${nextNoteType}, beat ${nextNote.beat}) is not allowed within ${currentLongType} range (${currentNote._origIndex}, beat ${currentNote.beat})`);
                        }
                    }
                } else if (Math.abs(nextTime - currentEndTime) <= EPSILON) {
                    continue;
                } else {
                    break;
                }
            }
        }
    }

    // _origIndex 제거 후 반환
    const cleanedNotes = validatedNotes.map(n => {
        const { _origIndex, ...rest } = n;
        return rest;
    });

    findIgnoredAngleOffsets(cleanedNotes).forEach(({ index, angleOffset }) => {
        warnings.push(
            `노트 #${index + 1}: angleOffset ${angleOffset}도가 무시됩니다 — direction이 미지정이라 직전 노트의 각도를 상속합니다.`
        );
    });

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        notes: cleanedNotes,
        sectionOffsets // save-json 핸들러에서 재사용 가능하도록 반환
    };
}

// 노트 데이터를 JSON 형식으로 변환
// sectionOffset: 이 노트가 속한 구간의 시작 절대시간(초). 기본값 0 (역호환)
export function noteToJsonFormat(note, globalBpm, globalSubdivisions, preDelaySeconds, sectionOffset = 0) {
    const timing = getNoteTimingParams(note, globalBpm, globalSubdivisions);
    let originalTime, finalTime;

    if (note.beat === 0 && note.type === "direction" && sectionOffset === 0) {
        // beat 0 direction 노트는 구간 0에서만 finalTime = 0 (기존 동작 유지)
        originalTime = 0;
        finalTime = 0;
    } else if ((note.type === "tab" || note.type === "longtab") &&
               note.hasOwnProperty('fadeDirectTime')) {
        // fade 구간의 tab/longtab 노트는 저장된 직접 시간값 사용
        finalTime = note.fadeDirectTime;
        originalTime = finalTime - preDelaySeconds;
    } else {
        originalTime = sectionOffset + beatToTime(note.beat, timing.bpm, timing.subdivisions);
        finalTime = originalTime + preDelaySeconds;
    }

    const noteType = convertNoteTypeToExternal(note.type);
    const longTimeInSeconds = (note.isLong && note.longTime > 0) ?
        calculateLongNoteTime(note, globalBpm, globalSubdivisions) : 0;

    const result = {
        beat: note.beat,
        bpm: note.bpm || globalBpm,
        subdivisions: note.subdivisions || globalSubdivisions,
        originalTime: originalTime,
        musicTime: 3.0 + originalTime, // MUSIC_START_TIME
        finalTime: finalTime,
        isLong: note.isLong || false,
        longTime: longTimeInSeconds,
        longTimeBeat: note.longTime || 0,
        noteType: noteType,
        direction: note.direction || "none",
        fade: note.fade || false  // BPM fade 여부 (boolean)
    };

    // 기본값 0이면 필드를 넣지 않는다 — 기존 차트가 그대로 재현되어야 한다
    if (note.angleOffset) {
        result.angleOffset = note.angleOffset;
    }

    // bezier도 기본값(곡률/bias 0)이면 필드 부재로 직렬화한다(§2.3, 클라와 합의).
    // wait Node는 곡선이 정의되지 않으므로 값이 있어도 내보내지 않는다(솔버가 무시하는 값이라 차트에 남기지 않음).
    const bezier = (note.type === "node" && note.wait) ? null : normalizeBezier(note.bezier);
    if (bezier) {
        result.bezier = bezier;
    }

    // Node 타입 노트의 경우 isWait 필드 추가
    if (note.type === "node") {
        result.isWait = note.wait || false;
    }

    // 모든 타입에서 beatReset이 true인 경우 isBeatReset 필드 추가
    if (note.beatReset) {
        result.isBeatReset = true;
    }

    return result;
}

// JSON 형식을 노트 데이터로 변환
export function jsonToNoteFormat(jsonNote, globalBpm, globalSubdivisions) {
    const beat = jsonNote.beat !== undefined ? jsonNote.beat : 
        timeToBeat(jsonNote.time || 0, globalBpm, globalSubdivisions);
    
    const type = convertExternalToNoteType(jsonNote.noteType);
    
    let longTimeBeat = 0;
    if (jsonNote.longTimeBeat !== undefined) {
        longTimeBeat = jsonNote.longTimeBeat;
    } else if (jsonNote.longTime !== undefined && jsonNote.longTime > 0) {
        const noteBpm = jsonNote.bpm || globalBpm;
        const noteSubdivisions = jsonNote.subdivisions || globalSubdivisions;
        longTimeBeat = timeToBeat(jsonNote.longTime, noteBpm, noteSubdivisions);
    }
    
    const noteData = {
        type: type,
        beat: beat,
        direction: jsonNote.direction || "none",
        isLong: jsonNote.isLong || false,
        longTime: longTimeBeat,
        bpm: jsonNote.bpm || globalBpm,
        subdivisions: jsonNote.subdivisions || globalSubdivisions,
        fade: jsonNote.fade || false,  // BPM fade 여부 (boolean)
        angleOffset: clampAngleOffset(jsonNote.angleOffset)
    };

    // bezier는 기본값이면 필드 부재 → 불러온 노트에도 달지 않는다(export와 대칭, 왕복 불변).
    const bezier = normalizeBezier(jsonNote.bezier);
    if (bezier) {
        noteData.bezier = bezier;
    }

    // Node 타입 노트의 경우 wait, beatReset 필드 추가
    if (type === "node") {
        noteData.wait = jsonNote.isWait || false;
        noteData.beatReset = jsonNote.isBeatReset || false; // 구버전 파일: undefined → false (역호환)
    }

    return noteData;
}

// 노트 리스트를 시간순으로 정렬
// 구간(section) 내에서만 정렬 - 구간 경계는 절대 변경하지 않음
// 구간 정의: 최초 시작 또는 reset 이후부터 ~ reset 토글이 체크된 노트까지
// beatReset 노트는 해당 구간의 마지막 노트
export function sortNotesByTime(notes, globalBpm, globalSubdivisions) {
    if (notes.length === 0) return [];

    // 작업용 배열 복사 (원본 노트 객체도 복사하여 수정 방지)
    const workNotes = notes.map(n => ({ ...n }));

    // sectionIndex 계산 (현재 배열 순서 기준)
    recomputeSectionIndices(workNotes);

    // 구간별로 노트 그룹화
    const sections = new Map();
    workNotes.forEach((note, originalIndex) => {
        const sectionIdx = note.sectionIndex || 0;
        if (!sections.has(sectionIdx)) {
            sections.set(sectionIdx, []);
        }
        sections.get(sectionIdx).push({ note, originalIndex });
    });

    // 각 구간 내에서 실제 시간 기준으로 정렬
    // beatReset 노트는 항상 해당 구간의 맨 뒤에 위치 (구간의 마지막 노트)
    // 주의: subdivisions가 다를 수 있으므로 beat가 아닌 실제 시간으로 비교해야 함
    sections.forEach((sectionNotes, sectionIdx) => {
        sectionNotes.sort((a, b) => {
            // beatReset 노트는 구간의 맨 뒤로 (구간의 마지막 노트)
            if (a.note.beatReset && !b.note.beatReset) return 1;
            if (!a.note.beatReset && b.note.beatReset) return -1;

            // 실제 시간으로 정렬 (beat, bpm, subdivisions를 모두 고려)
            const timeA = beatToTime(a.note.beat, a.note.bpm || globalBpm, a.note.subdivisions || globalSubdivisions);
            const timeB = beatToTime(b.note.beat, b.note.bpm || globalBpm, b.note.subdivisions || globalSubdivisions);
            const timeDiff = timeA - timeB;
            if (Math.abs(timeDiff) > 0.0001) {
                return timeDiff;
            }
            // 동일 시간일 경우 원래 순서 유지
            return a.originalIndex - b.originalIndex;
        });
    });

    // 정렬된 구간들을 순서대로 합치기
    const sortedSectionKeys = Array.from(sections.keys()).sort((a, b) => a - b);
    const result = [];
    sortedSectionKeys.forEach(sectionIdx => {
        sections.get(sectionIdx).forEach(item => {
            result.push(item.note);
        });
    });

    // 최종 sectionIndex 재계산
    recomputeSectionIndices(result);

    return result;
}

// 초기 direction 노트 확인 및 추가
export function ensureInitialDirectionNote(notes) {
    const initialDirectionNote = notes.find(n => n.beat === 0 && n.type === "direction");
    
    if (!initialDirectionNote) {
        // beat 0에 direction 노트가 없으면 새로 추가
        notes.unshift({
            type: "direction",
            beat: 0,
            direction: "right",
            isLong: false,
            longTime: 0,
            bpm: 120,
            subdivisions: 16
        });
    } else if (initialDirectionNote.direction === "none") {
        // beat 0에 direction 노트가 있지만 none이면 right로 변경
        initialDirectionNote.direction = "right";
    }
}

// 마지막 direction 노트 확인 및 추가 (마지막 노트로부터 3초 간격)
export function ensureFinalDirectionNote(notes, globalBpm, globalSubdivisions) {
    if (notes.length === 0) return;

    // 시간순으로 정렬하여 마지막 노트 찾기
    const sortedNotes = sortNotesByTime(notes, globalBpm, globalSubdivisions);
    const lastNote = sortedNotes[sortedNotes.length - 1];

    if (!lastNote || (lastNote.type === "direction" && lastNote.direction === "none")) {
        return; // 이미 Direction Type None이 마지막에 있음
    }

    // 마지막 노트의 절대시간 계산 (구간 오프셋 포함)
    const sectionOffsets = calculateSectionOffsets(notes, globalBpm, globalSubdivisions);
    const lastNoteOrigIdx = notes.indexOf(lastNote);
    const lastNoteSectionOffset = (lastNoteOrigIdx >= 0) ? (sectionOffsets[lastNoteOrigIdx] || 0) : 0;

    const lastNoteTiming = getNoteTimingParams(lastNote, globalBpm, globalSubdivisions);
    const lastNoteTime = lastNoteSectionOffset + beatToTime(lastNote.beat, lastNoteTiming.bpm, lastNoteTiming.subdivisions);
    let lastNoteEndTime = lastNoteTime;

    if (lastNote.isLong && lastNote.longTime > 0) {
        const longTimeInSeconds = calculateLongNoteTime(lastNote, globalBpm, globalSubdivisions);
        lastNoteEndTime = lastNoteTime + longTimeInSeconds;
    }

    // Long Note인 경우 1초, 일반 노트인 경우 3초 후
    const delayTime = (lastNote.isLong && lastNote.longTime > 0) ? 1.0 : 3.0;
    // finalTime = 절대시간 기준. 마지막 구간의 sectionOffset을 뺀 상대 beat로 변환
    const lastSectionOffset = sectionOffsets[sectionOffsets.length - 1] || 0;
    const relativeEndTime = lastNoteEndTime - lastSectionOffset + delayTime;
    const finalBeat = timeToBeat(relativeEndTime, globalBpm, globalSubdivisions);

    // 마지막에 Direction Type None 노트 추가 (마지막 구간 기준 상대 beat)
    notes.push({
        type: "direction",
        beat: Math.round(finalBeat),
        direction: "none",
        isLong: false,
        longTime: 0,
        bpm: globalBpm,
        subdivisions: globalSubdivisions
    });
}

// 노트 복제
export function cloneNote(note) {
    const cloned = {
        type: note.type,
        beat: note.beat,
        direction: note.direction || "none",
        isLong: note.isLong || false,
        longTime: note.longTime || 0,
        bpm: note.bpm,
        subdivisions: note.subdivisions,
        wait: note.wait || false,
        beatReset: note.beatReset || false,
        fade: note.fade || false,
        angleOffset: note.angleOffset || 0
    };
    // 곡선은 있을 때만 복제 대상에 얕은 복사로 옮긴다(참조 공유 방지).
    const bezier = normalizeBezier(note.bezier);
    if (bezier) cloned.bezier = bezier;
    return cloned;
}

// 노트 타입별 색상 가져오기
export function getNoteColor(noteType) {
    const colors = {
        "tab": "#4CAF50",
        "direction": "#2196F3", 
        "both": "#FF9800",
        "longtab": "#FF5722",
        "longdirection": "#03A9F4",
        "longboth": "#E91E63",
        "node": "#607D8B"
    };
    return colors[noteType] || "#4CAF50";
}